import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AssignmentTurnedInRounded from '@mui/icons-material/AssignmentTurnedInRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import WorkspacesRounded from '@mui/icons-material/WorkspacesRounded';
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../context/AuthContext';
import IndustrialWorkflowCaseWorkReportsPanel from '../components/IndustrialWorkflowCaseWorkReportsPanel';
import {
  completeIndustrialWorkflowTask,
  getIndustrialWorkflowOperatorMyWork,
  getIndustrialWorkflowsContext,
  listIndustrialWorkflowWorkReports,
  startIndustrialWorkflowTask,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowOperationsTaskItem,
  IndustrialWorkflowWorkReportValue,
} from '../types/industrialWorkflows';

function hasPermission(
  permissions: string[] | undefined,
  permission: string,
) {
  return Boolean(permissions?.includes(permission));
}

function formatDateTime(value: string | null) {
  if (!value) return null;

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function getCaseTitle(task: IndustrialWorkflowOperationsTaskItem) {
  return (
    task.case.title
    || task.case.reference_code
    || 'Expediente'
  );
}

function formatCaseFieldLabel(code: string) {
  return code
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, (character) =>
      character.toUpperCase(),
    );
}

function formatCaseFieldValue(value: unknown) {
  if (
    value === null
    || value === undefined
    || value === ''
  ) {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  if (Array.isArray(value)) {
    return value.length
      ? value.map((item) => String(item)).join(', ')
      : '—';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function isEmptyWorkReportValue(value: unknown) {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return !value.trim();
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
}

function getCaseFieldEntries(
  task: IndustrialWorkflowOperationsTaskItem,
) {
  return Object.entries(task.case.field_values ?? {})
    .filter(([, value]) => (
      value !== null
      && value !== undefined
      && value !== ''
    ))
    .sort(([leftCode], [rightCode]) =>
      leftCode.localeCompare(rightCode),
    );
}

function getWorkReportStatusLabel(
  status: IndustrialWorkflowOperationsTaskItem['work_report_status'],
) {
  if (status === 'completed') return 'Parte completado';
  if (status === 'cancelled') return 'Parte cancelado';
  if (status === 'draft') return 'Parte pendiente';

  return 'Sin parte asociado';
}

function getWorkReportStatusTone(
  status: IndustrialWorkflowOperationsTaskItem['work_report_status'],
) {
  if (status === 'completed') {
    return {
      color: '#bbf7d0',
      bgcolor: alpha('#22c55e', 0.15),
    };
  }

  if (status === 'cancelled') {
    return {
      color: '#cbd5e1',
      bgcolor: alpha('#64748b', 0.13),
    };
  }

  return {
    color: '#fde68a',
    bgcolor: alpha('#f59e0b', 0.16),
  };
}

function getTaskDueTone(
  dueStatus: IndustrialWorkflowOperationsTaskItem['due_status'],
) {
  if (dueStatus === 'overdue') {
    return {
      color: '#fecaca',
      bgcolor: alpha('#ef4444', 0.16),
    };
  }

  if (dueStatus === 'today') {
    return {
      color: '#fde68a',
      bgcolor: alpha('#f59e0b', 0.16),
    };
  }

  return {
    color: '#cbd5e1',
    bgcolor: alpha('#64748b', 0.13),
  };
}

export default function IndustrialWorkflowsOperatorPage() {
  const { user, signOut } = useAuth();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const workflowsContextQuery = useQuery({
    queryKey: ['industrial-workflows-context'],
    queryFn: getIndustrialWorkflowsContext,
    retry: false,
  });

  const permissions =
    workflowsContextQuery.data?.permissions ?? [];

  const canViewTasks =
    workflowsContextQuery.data?.has_access === true
    && hasPermission(permissions, 'workflows:tasks:view');

  const canOperateTasks =
    hasPermission(permissions, 'workflows:tasks:operate')
    || hasPermission(permissions, 'workflows:tasks:manage');

  const operatorMyWorkQuery = useQuery({
    queryKey: ['industrial-workflows-operator-my-work'],
    queryFn: getIndustrialWorkflowOperatorMyWork,
    enabled: canViewTasks,
    retry: false,
    refetchInterval: 30000,
  });

  const assignedTasks =
    operatorMyWorkQuery.data?.assigned_tasks ?? [];

  const availableTasks =
    operatorMyWorkQuery.data?.available_tasks ?? [];

  const totalTasks =
    operatorMyWorkQuery.data?.summary.total_tasks ?? 0;

  const hasTasks =
    totalTasks > 0;

  const interventionSections = useMemo(() => {
    const rows = [
      ...assignedTasks.map((task) => ({
        task,
        mode: 'assigned' as const,
      })),
      ...availableTasks.map((task) => ({
        task,
        mode: 'available' as const,
      })),
    ];

    const sectionByKey = new Map<
      string,
      {
        key: string;
        title: string;
        workAreaName: string | null;
        rows: Array<{
          task: IndustrialWorkflowOperationsTaskItem;
          mode: 'assigned' | 'available';
        }>;
      }
    >();

    rows.forEach((row) => {
      const sectionKey = [
        row.task.title,
        row.task.work_area_id ?? '',
      ].join('::');

      const currentSection =
        sectionByKey.get(sectionKey) ?? {
          key: sectionKey,
          title: row.task.title || 'Trabajo operativo',
          workAreaName: row.task.work_area_name,
          rows: [],
        };

      currentSection.rows.push(row);
      sectionByKey.set(sectionKey, currentSection);
    });

    return Array.from(sectionByKey.values()).sort((left, right) =>
      left.title.localeCompare(right.title),
    );
  }, [assignedTasks, availableTasks]);

  const operatorRows = useMemo(
    () =>
      interventionSections.flatMap((section) =>
        section.rows.map((row) => ({
          ...row,
          key: `${row.mode}-${row.task.id}`,
          sectionKey: section.key,
          sectionTitle: section.title,
          sectionWorkAreaName: section.workAreaName,
        })),
      ),
    [interventionSections],
  );

  const [
    selectedOperatorRowKey,
    setSelectedOperatorRowKey,
  ] = useState<string | null>(null);

  const selectedOperatorRow = useMemo(
    () =>
      operatorRows.find(
        (row) => row.key === selectedOperatorRowKey,
      )
      ?? operatorRows[0]
      ?? null,
    [operatorRows, selectedOperatorRowKey],
  );

  useEffect(
    () => {
      if (!operatorRows.length) {
        if (selectedOperatorRowKey !== null) {
          setSelectedOperatorRowKey(null);
        }

        return;
      }

      if (
        selectedOperatorRowKey
        && operatorRows.some(
          (row) => row.key === selectedOperatorRowKey,
        )
      ) {
        return;
      }

      setSelectedOperatorRowKey(operatorRows[0].key);
    },
    [operatorRows, selectedOperatorRowKey],
  );

  const selectedWorkReportsQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-reports',
      selectedOperatorRow?.task.case_id,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkReports(
        selectedOperatorRow?.task.case_id as string,
        true,
      ),
    enabled: Boolean(selectedOperatorRow?.task.case_id),
    retry: false,
    refetchOnMount: 'always',
  });

  const selectedLinkedWorkReport = useMemo(
    () => {
      const workReportId =
        selectedOperatorRow?.task.work_report_id;

      if (!workReportId) {
        return null;
      }

      return (
        (selectedWorkReportsQuery.data ?? []).find(
          (report) => report.id === workReportId,
        )
        ?? null
      );
    },
    [
      selectedOperatorRow?.task.work_report_id,
      selectedWorkReportsQuery.data,
    ],
  );

  const selectedWorkReportValues = useMemo(
    () =>
      (selectedLinkedWorkReport?.values ?? [])
        .filter((fieldValue) => fieldValue.is_active)
        .sort(
          (left, right) =>
            left.display_order - right.display_order
            || left.label.localeCompare(right.label),
        ),
    [selectedLinkedWorkReport],
  );

  const selectedWorkReportCaseValues = useMemo(
    () =>
      selectedWorkReportValues.filter(
        (fieldValue) =>
          fieldValue.field_source === 'case_field',
      ),
    [selectedWorkReportValues],
  );

  const selectedWorkReportOwnValues = useMemo(
    () =>
      selectedWorkReportValues.filter(
        (fieldValue) =>
          fieldValue.field_source === 'work_report_field',
      ),
    [selectedWorkReportValues],
  );

  const selectedMissingRequiredReportValues = useMemo(
    () =>
      selectedWorkReportValues.filter(
        (fieldValue) =>
          fieldValue.is_required_to_complete
          && isEmptyWorkReportValue(fieldValue.value),
      ),
    [selectedWorkReportValues],
  );

  const generatedAtLabel = useMemo(
    () =>
      operatorMyWorkQuery.data?.generated_at
        ? formatDateTime(operatorMyWorkQuery.data.generated_at)
        : null,
    [operatorMyWorkQuery.data?.generated_at],
  );

  const [
    workReportTaskContext,
    setWorkReportTaskContext,
  ] =
    useState<IndustrialWorkflowOperationsTaskItem | null>(
      null,
    );

  const [
    requestedWorkReportId,
    setRequestedWorkReportId,
  ] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const startTaskMutation = useMutation({
    mutationFn: (task: IndustrialWorkflowOperationsTaskItem) =>
      startIndustrialWorkflowTask(
        task.case_id,
        task.id,
      ),
    onSuccess: async () => {
      await operatorMyWorkQuery.refetch();

      enqueueSnackbar(
        'Intervención iniciada. El expediente queda asignado a tu usuario.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : 'No se pudo iniciar la intervención.',
        {
          variant: 'error',
        },
      );
    },
  });

  const completeInterventionMutation = useMutation({
    mutationFn: (task: IndustrialWorkflowOperationsTaskItem) =>
      completeIndustrialWorkflowTask(
        task.case_id,
        task.id,
      ),
    onSuccess: async () => {
      await operatorMyWorkQuery.refetch();

      enqueueSnackbar(
        'Intervención finalizada. El expediente queda listo para avanzar.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : 'No se pudo finalizar la intervención.',
        {
          variant: 'error',
        },
      );
    },
  });

  const activeUserName =
    user?.user_metadata?.full_name?.trim()
    || user?.email?.split('@')[0]
    || 'Usuario de fábrica';

  const activeUserSubtitle =
    user?.email || 'Sesión activa';

  const handleRequestLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleConfirmLogout = async () => {
    try {
      setLogoutDialogOpen(false);
      await signOut();
      window.location.href = '/factory/login';
    } catch (error) {
      console.error(
        'Industrial workflows operator logout error',
        error,
      );
    }
  };

  const renderTaskCard = (
    task: IndustrialWorkflowOperationsTaskItem,
    mode: 'assigned' | 'available',
  ) => {
    const rowKey = `${mode}-${task.id}`;
    const isSelected = selectedOperatorRowKey === rowKey;
    const caseTitle = getCaseTitle(task);
    const dueDateLabel = formatDateTime(task.due_at);
    const dueTone = getTaskDueTone(task.due_status);
    const workReportTone =
      getWorkReportStatusTone(task.work_report_status);
    const workReportStatusLabel =
      getWorkReportStatusLabel(task.work_report_status);
    const caseFieldEntries =
      getCaseFieldEntries(task).slice(0, 2);

    return (
      <Paper
        key={rowKey}
        elevation={0}
        onClick={() => setSelectedOperatorRowKey(rowKey)}
        sx={{
          cursor: 'pointer',
          borderRadius: 2.25,
          border: `1px solid ${alpha(
            isSelected ? '#38bdf8' : '#94a3b8',
            isSelected ? 0.34 : 0.12,
          )}`,
          bgcolor: isSelected
            ? alpha('#0284c7', 0.16)
            : 'rgba(2,6,23,0.28)',
          px: 0.9,
          py: 0.75,
          transition: 'border-color 120ms ease, background-color 120ms ease',
          '&:hover': {
            borderColor: alpha('#38bdf8', 0.28),
            bgcolor: alpha('#0284c7', 0.12),
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack
            spacing={0.35}
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: '0.92rem',
                lineHeight: 1.15,
                wordBreak: 'break-word',
              }}
            >
              {caseTitle}
            </Typography>

            {caseFieldEntries.map(([code, value]) => (
              <Typography
                key={code}
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.74rem',
                  lineHeight: 1.2,
                }}
              >
                {formatCaseFieldLabel(code)}:{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#e2e8f0',
                    fontWeight: 850,
                  }}
                >
                  {formatCaseFieldValue(value)}
                </Box>
              </Typography>
            ))}
          </Stack>

          <Stack
            spacing={0.45}
            alignItems="flex-end"
            sx={{ flexShrink: 0 }}
          >
            <Chip
              size="small"
              label={dueDateLabel || 'Sin plazo'}
              sx={{
                height: 22,
                color: dueTone.color,
                bgcolor: dueTone.bgcolor,
                fontWeight: 850,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />

            {task.work_report_id ? (
              <Chip
                size="small"
                label={workReportStatusLabel}
                sx={{
                  height: 22,
                  color: workReportTone.color,
                  bgcolor: workReportTone.bgcolor,
                  fontWeight: 850,
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderReportValueGroup = (
    title: string,
    values: IndustrialWorkflowWorkReportValue[],
  ) => {
    if (!values.length) {
      return null;
    }

    return (
      <Stack spacing={0.75}>
        <Typography
          sx={{
            color: '#cbd5e1',
            fontWeight: 950,
            fontSize: '0.84rem',
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 0.65,
          }}
        >
          {values.map((fieldValue) => (
            <Box
              key={fieldValue.field_definition_id}
              sx={{
                minWidth: 0,
                borderRadius: 2,
                border: `1px solid ${alpha(
                  fieldValue.is_required_to_complete
                  && isEmptyWorkReportValue(fieldValue.value)
                    ? '#f59e0b'
                    : '#94a3b8',
                  fieldValue.is_required_to_complete
                  && isEmptyWorkReportValue(fieldValue.value)
                    ? 0.26
                    : 0.12,
                )}`,
                bgcolor: 'rgba(2,6,23,0.24)',
                px: 0.9,
                py: 0.7,
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.64rem',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: 0.45,
                }}
              >
                {fieldValue.label}
              </Typography>

              <Typography
                sx={{
                  color: isEmptyWorkReportValue(fieldValue.value)
                    ? '#94a3b8'
                    : '#e2e8f0',
                  fontSize: '0.84rem',
                  fontWeight: 850,
                  mt: 0.12,
                  wordBreak: 'break-word',
                }}
              >
                {formatCaseFieldValue(fieldValue.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    );
  };

  const renderSelectedTaskDetail = () => {
    if (!selectedOperatorRow) {
      return (
        <Typography
          sx={{
            color: '#94a3b8',
            fontSize: '0.9rem',
          }}
        >
          Selecciona un expediente.
        </Typography>
      );
    }

    const task = selectedOperatorRow.task;
    const mode = selectedOperatorRow.mode;
    const caseTitle = getCaseTitle(task);
    const caseFieldEntries = getCaseFieldEntries(task);
    const dueDateLabel = formatDateTime(task.due_at);
    const dueTone = getTaskDueTone(task.due_status);
    const workReportTone =
      getWorkReportStatusTone(task.work_report_status);
    const workReportStatusLabel =
      getWorkReportStatusLabel(task.work_report_status);

    const canStartTask =
      canOperateTasks
      && (
        task.status === 'pending'
        || task.status === 'blocked'
      );

    const canOpenWorkReport =
      canOperateTasks
      && Boolean(task.work_report_id)
      && mode === 'assigned'
      && task.status !== 'completed'
      && task.status !== 'cancelled';

    const canCompleteIntervention =
      canOperateTasks
      && mode === 'assigned'
      && task.status === 'in_progress'
      && (
        !task.work_report_id
        || task.work_report_status === 'completed'
      );

    return (
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: '1.18rem',
                lineHeight: 1.15,
              }}
            >
              {caseTitle}
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.82rem',
                mt: 0.3,
              }}
            >
              {selectedOperatorRow.sectionTitle}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              minWidth: { xs: '100%', md: 190 },
              borderRadius: 2,
              border: `1px solid ${alpha(dueTone.color, 0.28)}`,
              bgcolor: dueTone.bgcolor,
              px: 0.95,
              py: 0.7,
            }}
          >
            <Typography
              sx={{
                color: '#cbd5e1',
                fontSize: '0.62rem',
                fontWeight: 950,
                textTransform: 'uppercase',
                letterSpacing: 0.55,
              }}
            >
              Plazo
            </Typography>

            <Typography
              sx={{
                color: dueTone.color,
                fontSize: '0.96rem',
                fontWeight: 950,
                mt: 0.15,
              }}
            >
              {dueDateLabel || 'Sin plazo'}
            </Typography>
          </Paper>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha('#94a3b8', 0.12)}`,
            bgcolor: 'rgba(2,6,23,0.22)',
            px: 1,
            py: 0.9,
          }}
        >
          <Stack spacing={0.8}>
            <Typography
              sx={{
                color: '#cbd5e1',
                fontWeight: 950,
                fontSize: '0.84rem',
              }}
            >
              Datos del expediente
            </Typography>

            {caseFieldEntries.length ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 0.65,
                }}
              >
                {caseFieldEntries.map(([code, value]) => (
                  <Box
                    key={code}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                      bgcolor: 'rgba(15,23,42,0.28)',
                      px: 0.9,
                      py: 0.65,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.64rem',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: 0.45,
                      }}
                    >
                      {formatCaseFieldLabel(code)}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#e2e8f0',
                        fontSize: '0.84rem',
                        fontWeight: 850,
                        mt: 0.12,
                        wordBreak: 'break-word',
                      }}
                    >
                      {formatCaseFieldValue(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.82rem',
                }}
              >
                Sin datos visibles.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(workReportTone.color, 0.22)}`,
            bgcolor: alpha('#020617', 0.34),
            px: 1,
            py: 0.9,
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={0.75}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={0.6}
                  alignItems="center"
                >
                  <FactCheckRounded
                    sx={{
                      color: workReportTone.color,
                      fontSize: 19,
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 950,
                    }}
                  >
                    Parte de trabajo
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: workReportTone.color,
                    fontWeight: 900,
                    fontSize: '0.84rem',
                    mt: 0.25,
                  }}
                >
                  {workReportStatusLabel}
                </Typography>
              </Box>

              {selectedMissingRequiredReportValues.length ? (
                <Chip
                  size="small"
                  label={`${selectedMissingRequiredReportValues.length} obligatorio${
                    selectedMissingRequiredReportValues.length === 1
                      ? ''
                      : 's'
                  } pendiente${
                    selectedMissingRequiredReportValues.length === 1
                      ? ''
                      : 's'
                  }`}
                  sx={{
                    alignSelf: {
                      xs: 'flex-start',
                      sm: 'center',
                    },
                    color: '#fde68a',
                    bgcolor: alpha('#f59e0b', 0.15),
                    fontWeight: 900,
                  }}
                />
              ) : null}
            </Stack>

            {selectedWorkReportsQuery.isFetching
            && task.work_report_id ? (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ color: '#94a3b8' }}
              >
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: '0.82rem' }}>
                  Cargando datos del parte...
                </Typography>
              </Stack>
            ) : null}

            {selectedLinkedWorkReport ? (
              <Stack spacing={1}>
                {renderReportValueGroup(
                  'Datos del expediente usados por el parte',
                  selectedWorkReportCaseValues,
                )}

                {renderReportValueGroup(
                  'Datos del parte actual',
                  selectedWorkReportOwnValues,
                )}

                {!selectedWorkReportValues.length ? (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.82rem',
                    }}
                  >
                    Este parte no tiene campos configurados.
                  </Typography>
                ) : null}
              </Stack>
            ) : task.work_report_id
              && !selectedWorkReportsQuery.isFetching ? (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.82rem',
                }}
              >
                No se pudieron cargar los datos del parte asociado.
              </Typography>
            ) : !task.work_report_id ? (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.82rem',
                }}
              >
                Esta intervención no tiene parte asociado.
              </Typography>
            ) : null}

            {canOperateTasks ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.65}
                justifyContent="flex-end"
              >
                {canStartTask ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrowRounded />}
                    disabled={startTaskMutation.isPending}
                    onClick={() =>
                      startTaskMutation.mutate(task)
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 950,
                    }}
                  >
                    {mode === 'available'
                      ? 'Tomar intervención'
                      : 'Iniciar intervención'}
                  </Button>
                ) : null}

                {canOpenWorkReport ? (
                  <Button
                    size="small"
                    variant={
                      task.work_report_status === 'completed'
                        ? 'outlined'
                        : 'contained'
                    }
                    startIcon={<FactCheckRounded />}
                    onClick={() => {
                      setWorkReportTaskContext(task);
                      setRequestedWorkReportId(
                        task.work_report_id,
                      );
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 950,
                    }}
                  >
                    Abrir parte
                  </Button>
                ) : null}

                {canCompleteIntervention ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleRounded />}
                    disabled={
                      completeInterventionMutation.isPending
                    }
                    onClick={() =>
                      completeInterventionMutation.mutate(task)
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 950,
                    }}
                  >
                    Finalizar intervención
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#020617',
        color: '#f8fafc',
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1.5, md: 2.5 },
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
            bgcolor: alpha('#0f172a', 0.92),
            px: { xs: 1.5, md: 2 },
            py: 1.25,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: alpha('#38bdf8', 0.12),
                  color: '#38bdf8',
                  border: `1px solid ${alpha('#38bdf8', 0.28)}`,
                }}
              >
                <AccountCircle fontSize="medium" />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '0.96rem',
                    lineHeight: 1.2,
                  }}
                >
                  {activeUserName}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    color: '#94a3b8',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    lineHeight: 1.2,
                  }}
                >
                  {activeUserSubtitle}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              color="error"
              onClick={handleRequestLogout}
              sx={{
                alignSelf: { xs: 'stretch', sm: 'center' },
                fontWeight: 800,
                borderRadius: 2.5,
                textTransform: 'none',
                px: 2,
              }}
            >
              Cerrar sesión
            </Button>
          </Stack>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha('#38bdf8', 0.18)}`,
            bgcolor: alpha('#0f172a', 0.86),
            px: { xs: 1.5, md: 2.5 },
            py: { xs: 1.75, md: 2.25 },
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <AssignmentTurnedInRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: '1.35rem',
                      fontWeight: 950,
                    }}
                  >
                    Expedientes por intervención
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.92rem',
                    }}
                  >
                    Expedientes pendientes de ejecución.
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {workflowsContextQuery.isLoading ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: '#94a3b8' }}
              >
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: '0.9rem' }}>
                  Comprobando acceso al organizador...
                </Typography>
              </Stack>
            ) : null}

            {workflowsContextQuery.isError ? (
              <Alert severity="error" variant="outlined">
                No se pudo comprobar tu acceso al organizador.
              </Alert>
            ) : null}

            {!workflowsContextQuery.isLoading
            && !workflowsContextQuery.isError
            && !canViewTasks ? (
              <Alert
                severity="warning"
                variant="outlined"
                icon={<LockRounded />}
              >
                Tu usuario no tiene permisos para consultar intervenciones del organizador.
              </Alert>
            ) : null}

            {canViewTasks ? (
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', lg: 'flex-start' }}
                  spacing={1}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(auto-fit, minmax(160px, 1fr))',
                      },
                      gap: 0.75,
                      alignItems: 'stretch',
                      flex: 1,
                    }}
                  >
                    {[
                      {
                        label: 'Expedientes',
                        value: totalTasks,
                      },
                      ...interventionSections.map((section) => ({
                        label: section.title,
                        value: section.rows.length,
                      })),
                    ].map((item) => (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          borderRadius: 2.25,
                          border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                          bgcolor: 'rgba(2,6,23,0.24)',
                          px: 0.9,
                          py: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.62rem',
                            fontWeight: 900,
                            letterSpacing: 0.5,
                          }}
                        >
                          {item.label}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontSize: '1.05rem',
                            fontWeight: 950,
                            mt: 0.15,
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<RefreshRounded />}
                    onClick={() => operatorMyWorkQuery.refetch()}
                    disabled={operatorMyWorkQuery.isFetching}
                    sx={{
                      minHeight: 56,
                      minWidth: { xs: '100%', lg: 160 },
                      alignSelf: { xs: 'stretch', lg: 'flex-start' },
                      textTransform: 'none',
                      fontWeight: 850,
                    }}
                  >
                    Actualizar
                  </Button>
                </Stack>

                {generatedAtLabel ? (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.76rem',
                    }}
                  >
                    Última actualización: {generatedAtLabel}
                  </Typography>
                ) : null}

                {operatorMyWorkQuery.isLoading ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ color: '#94a3b8' }}
                  >
                    <CircularProgress size={18} />
                    <Typography sx={{ fontSize: '0.9rem' }}>
                      Cargando expedientes pendientes...
                    </Typography>
                  </Stack>
                ) : null}

                {operatorMyWorkQuery.isError ? (
                  <Alert severity="error" variant="outlined">
                    No se pudo cargar tu puesto operativo.
                  </Alert>
                ) : null}

                {!operatorMyWorkQuery.isLoading
                && !operatorMyWorkQuery.isError
                && !hasTasks ? (
                  <Alert severity="info" variant="outlined">
                    No tienes expedientes pendientes en tus intervenciones.
                  </Alert>
                ) : null}

                {operatorRows.length ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        lg: 'minmax(320px, 0.72fr) minmax(0, 1.28fr)',
                      },
                      gap: 1,
                      alignItems: 'start',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha('#38bdf8', 0.16)}`,
                        bgcolor: 'rgba(15,23,42,0.44)',
                        px: 0.85,
                        py: 0.75,
                      }}
                    >
                      <Stack spacing={0.85}>
                        {interventionSections.map((section) => (
                          <Box key={section.key}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={0.75}
                              sx={{ mb: 0.55 }}
                            >
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 950,
                                  fontSize: '0.88rem',
                                }}
                              >
                                {section.title}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.45}
                                flexWrap="wrap"
                                useFlexGap
                                justifyContent="flex-end"
                              >
                                {section.workAreaName ? (
                                  <Chip
                                    size="small"
                                    icon={<WorkspacesRounded />}
                                    label={section.workAreaName}
                                    sx={{
                                      height: 22,
                                      color: '#bae6fd',
                                      bgcolor: alpha('#0284c7', 0.15),
                                      fontWeight: 850,
                                    }}
                                  />
                                ) : null}

                                <Chip
                                  size="small"
                                  label={`${section.rows.length}`}
                                  sx={{
                                    height: 22,
                                    color: '#cbd5e1',
                                    bgcolor: alpha('#64748b', 0.13),
                                    fontWeight: 850,
                                  }}
                                />
                              </Stack>
                            </Stack>

                            <Stack spacing={0.6}>
                              {section.rows.map((row) =>
                                renderTaskCard(row.task, row.mode),
                              )}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha('#38bdf8', 0.16)}`,
                        bgcolor: 'rgba(15,23,42,0.44)',
                        px: { xs: 0.95, md: 1.15 },
                        py: { xs: 0.95, md: 1.1 },
                      }}
                    >
                      {renderSelectedTaskDetail()}
                    </Paper>
                  </Box>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      {workReportTaskContext ? (
        <Box sx={{ display: 'none' }}>
          <IndustrialWorkflowCaseWorkReportsPanel
            workflowCase={workReportTaskContext.case}
            canManageWorkReports={false}
            operableWorkReportIds={
              workReportTaskContext.work_report_id
                ? [workReportTaskContext.work_report_id]
                : []
            }
            hiddenWorkReportIds={[]}
            requestedWorkReportId={requestedWorkReportId}
            onRequestedWorkReportConsumed={() =>
              setRequestedWorkReportId(null)
            }
          />
        </Box>
      ) : null}

      <Dialog open={logoutDialogOpen} onClose={handleCancelLogout}>
        <DialogTitle>Cerrar sesión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que quieres cerrar la sesión del puesto operativo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmLogout}
          >
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
